<template>
  <UserProfileStepper
    :user="formUser"
    :active-page="activePage"
    :page-dots="pageDots"
    :name="$auth.user.name"
    :saving="savingUser"
    :errors="$v[activePage]"
    :employment-type-img="employmentTypeImg"
    :postcode-img="postcodeImg"
    :contact-img="contactImg"
    :loading-user-organisations="loadingUserOrganisations"
    :saving-user-organisation="savingUserOrganisation"
    :selected-organisation="selectedOrganisation"
    :user-organisations="organisationList"
    :employment-options="Object.values($Constants.EMPLOYMENT_OPTIONS)"
    :corporate-account-signup="corporateAccountSignup"
    :employer-key="$Constants.USER_PROFILE_PAGES.employerDetails"
    :employment-type-key="$Constants.USER_PROFILE_PAGES.employmentType"
    :postcode-key="$Constants.USER_PROFILE_PAGES.postcode"
    :contact-number-key="$Constants.USER_PROFILE_PAGES.contactNumber"
    :organisation-selection-key="
      $Constants.USER_PROFILE_PAGES.organisationSelection
    "
    @update:page="activePage = $event"
    @skip="skip"
    @close="close"
    @update:user="formUser = $event"
    @update:selected-organisation="selectedOrganisation = $event"
    @update:corporate-account-signup="setCorporateAccountSignup"
    @save="saveAndNext"
  />
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { getAsset } from 'ecx.saigon.ui.common';
import validations from './validations';

export default {
  name: 'UserProfileWidget',
  data() {
    return {
      activePage: this.$Constants.USER_PROFILE_PAGES.employerDetails,
      formUser: { ...this.$Constants.INITIAL_USER },
      selectedOrganisation: { value: undefined, label: undefined },
      corporateAccountSignup: false,
      employmentTypeImg: undefined,
      postcodeImg: undefined,
      contactImg: undefined
    };
  },
  validations,
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      userOrganisations: (state) => state.user.userOrganisations,
      loadingUserOrganisations: (state) => state.user.loadingUserOrganisations,
      savingUserOrganisation: (state) => state.user.savingUserOrganisation,
      name: (state) => state.user.name,
      savingUser: (state) => state.user.savingUser
    }),
    ...mapGetters('user', [
      'profileSetupPage',
      'hasMultipleOrganisationMatches'
    ]),
    organisationList() {
      return this.userOrganisations.map((org) => ({
        value: org.orgId,
        label: org.organisation
      }));
    },
    pageDots() {
      let pages = Object.values(this.$Constants.USER_PROFILE_PAGES);
      let activePageIndex = pages.indexOf(this.activePage);
      return pages.map((page, index) => {
        let disable = index < activePageIndex ? false : this.$v[page].$invalid;
        if (
          page === this.$Constants.USER_PROFILE_PAGES.organisationSelection &&
          this.disableOrgSelection
        ) {
          disable = true;
        }
        return {
          id: page,
          active: this.activePage === page,
          disable
        };
      });
    },
    disableOrgSelection() {
      return this.user.hasOrganisation || !this.hasMultipleOrganisationMatches;
    },
    organisationSelectionActive() {
      return (
        this.activePage ===
        this.$Constants.USER_PROFILE_PAGES.organisationSelection
      );
    }
  },
  watch: {
    activePage() {
      if (this.organisationSelectionActive && !this.userOrganisations.length) {
        this.getUserOrganisations();
      }
    }
  },
  methods: {
    ...mapActions('user', [
      'saveUserProfile',
      'getUserOrganisations',
      'updateUserOrganisation'
    ]),
    skip() {
      if (!this.savingUser) {
        this.next();
      }
    },
    close() {
      if (!this.savingUser) {
        this.$emit('close');
      }
    },
    noCorporateEmailNotification() {
      if (
        this.activePage ===
          this.$Constants.USER_PROFILE_PAGES.employerDetails &&
        this.formUser.noCorporateEmail
      ) {
        this.SAIDialog({
          body: `Your employer is not currently set up in our system.
          Our team will contact your employer and then be in touch with you shortly.`,
          okText: 'Ok'
        });
      }
    },
    setCorporateAccountSignup(value) {
      this.formUser = {
        ...this.formUser,
        companyEmail: value ? this.$auth.user.email : undefined
      };
      this.corporateAccountSignup = value;
    },
    async saveAndNext() {
      this.$v[this.activePage].$touch();
      if (this.$v[this.activePage].$invalid) {
        return;
      }
      this.noCorporateEmailNotification();
      try {
        this.organisationSelectionActive
          ? await this.updateUserOrganisation(this.selectedOrganisation.value)
          : await this.saveUserProfile(this.formUser);
        this.next();
      } catch (err) {}
    },
    next() {
      const pageArray = Object.values(this.$Constants.USER_PROFILE_PAGES);
      const currentPageIndex = pageArray.indexOf(this.activePage);
      let nextPage = pageArray[currentPageIndex + 1];
      if (
        nextPage === this.$Constants.USER_PROFILE_PAGES.organisationSelection &&
        this.disableOrgSelection
      ) {
        nextPage = pageArray[currentPageIndex + 2];
      }
      if (currentPageIndex === pageArray.length - 1) {
        this.close();
      } else {
        this.activePage = nextPage;
      }
    }
  },
  async mounted() {
    this.activePage = this.profileSetupPage;
    this.formUser = { ...this.user.profile };

    this.employmentTypeImg = await getAsset(
      this.$Constants.PROFILE_SETUP_WORKTYPE_PATH
    );
    this.postcodeImg = await getAsset(
      this.$Constants.PROFILE_SETUP_POSTCODE_PATH
    );
    this.contactImg = await getAsset(
      this.$Constants.PROFILE_SETUP_CONTACT_PATH
    );
  },
  components: {
    UserProfileStepper: () =>
      import('../../components/organism/user-profile-stepper')
  }
};
</script>
